import { AxiosResponse, Canceler } from 'axios';
import settings from '../settings';
import { IApiResult, IPaginationResponseDto, ITransfer, ITransferInformation, ITransferMonthlyRoyaltyExpense } from '../types';
import api from './base';

export enum TransferSearchType {
  SeasonId = 'seasonId',
  StoryId = 'storyId',
}

export interface IFetchTransfersParams {
  searchKey?: number;
  searchType?: TransferSearchType;
}

export function fetchTransfersByQuarter(quarter: number): [Promise<AxiosResponse<ITransfer[]>>, Canceler] {
  const uri = `${settings.getApiURI()}/transfer-admin/v1/transfer-quarters/${quarter}`;
  return api.getWithCancel(uri);
}

export function fetchTransfersToBe(): [Promise<AxiosResponse<ITransfer[]>>, Canceler] {
  const uri = `${settings.getApiURI()}/transfer-admin/v1/to-be-transferred`;
  return api.getWithCancel(uri);
}

export function fetchTransferStoriesByTransferId(transferId: number): [Promise<AxiosResponse<ITransferInformation>>, Canceler] {
  const uri = `${settings.getApiURI()}/transfer-admin/v2/transfers/${transferId}/transfers-by-season`;
  return api.getWithCancel(uri);
}

export function fetchTransfersByQuarterWithSearchKey(
  quarter: number,
  params: IFetchTransfersParams,
): [Promise<AxiosResponse<ITransfer[]>>, Canceler] {
  const { searchType, searchKey } = params;
  const paramObj = !!searchType ? { [searchType]: searchKey } : {};

  const uri = `${settings.getApiURI()}/transfer-admin/v1/transfer-quarters/${quarter}`;

  return api.getWithCancel(uri, {
    params: {
      ...paramObj,
    },
  });
}

export function fetchToBeTransferredBySearchKey(quarter: number, params: IFetchTransfersParams): [Promise<AxiosResponse<ITransfer[]>>, Canceler] {
  const { searchType, searchKey } = params;
  const paramObj = !!searchType ? { [searchType]: searchKey } : {};

  const uri = `${settings.getApiURI()}/transfer-admin/v1/to-be-transferred`;

  return api.getWithCancel(uri, {
    params: {
      ...paramObj,
    },
  });
}

export function fetchTransfersByMonthlyWithSearchKey(
  month: number,
  page: number,
  limit: number,
  params: IFetchTransfersParams,
): [Promise<AxiosResponse<IPaginationResponseDto<ITransfer>>>, Canceler] {
  const { searchType, searchKey } = params;
  const paramObj = !!searchType ? { [searchType]: searchKey } : {};

  const uri = `${settings.getApiURI()}/transfer-admin/v1/transfer-months/${month}`;

  return api.getWithCancel(uri, {
    params: {
      page,
      limit,
      ...paramObj,
    },
  });
}

export function updateTransferStatus(transferId: number): Promise<AxiosResponse<IApiResult<ITransfer>>> {
  const uri = `${settings.getApiURI()}/transfer-admin/v1/transfers/${transferId}/transfer`;

  return api.http.post(uri);
}

export function fetchTransferMonthlyRoyaltyExpense(year: number): [Promise<AxiosResponse<ITransferMonthlyRoyaltyExpense[]>>, Canceler] {
  const uri = `${settings.getApiURI()}/transfer-admin/v1/monthly-royalty-expense`;
  return api.getWithCancel(uri, { params: { year } });
}

export function downloadMonthlyRoyaltyExpense(year: number): Promise<AxiosResponse<any>> {
  const uri = `${settings.getApiURI()}/transfer-admin/v1/download-monthly-royalty-expense`;

  return api.http.get(uri, {
    params: { year },
  });
}

export function downloadTransfers(quarter: number): Promise<AxiosResponse<any>> {
  let uri = `${settings.getApiURI()}/transfer-admin/v1`;

  if (quarter === 0) {
    uri = `${uri}/download-to-be-transferred`;
  } else {
    uri = `${uri}/download-transfer-quarters/${quarter}`;
  }

  return api.http.get(uri);
}

export function downloadSeasonTransfers(transferId: number): Promise<AxiosResponse<any>> {
  const uri = `${settings.getApiURI()}/transfer-admin/v2/transfers/${transferId}/download-transfers-by-season`;

  return api.http.get(uri);
}

export function downloadMonthlyTransfers(
  month: number,
  page: number,
  limit: number,
  summary: boolean,
  params: IFetchTransfersParams,
): Promise<AxiosResponse<any>> {
  const { searchType, searchKey } = params;
  const paramObj = !!searchType ? { [searchType]: searchKey } : {};

  const uri = `${settings.getApiURI()}/transfer-admin/v1/download-transfer-months/${month}`;

  return api.http.get(uri, {
    params: {
      page,
      limit,
      summary,
      ...paramObj,
    },
  });
}
